import { isUSBasedRegion } from 'src/config'
import { COUNTRY_CODES, CURRENCIES, DEFAULT_CURRENCY, EN, findCompatibleLanguageRegion, REGION_AVAILABLE_LANGUAGES, REGION_AVAILABLE_LOCALES } from 'src/i18n'
import AccountManager from 'src/models/account-manager/AccountManager'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Tax from 'src/models/order/Tax'
import Report from 'src/models/report/Report'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import Site from 'src/models/site/Site'

export const GIFT_CARD_EXPIRY_TERMS = {
  MONTHS: 'months',
  YEARS: 'years'
}
GIFT_CARD_EXPIRY_TERMS.ALL = _.values(GIFT_CARD_EXPIRY_TERMS)

export const MEMBERSHIP_RATE_DISPLAY_PERIODS = {
  MONTHLY: 1,
  YEARLY: 12
}
MEMBERSHIP_RATE_DISPLAY_PERIODS.ALL = _.values(MEMBERSHIP_RATE_DISPLAY_PERIODS)

export const ONE_TIME_CODE_LOGIN_TYPES = {
  EMAIL: 'email',
  PHONE: 'phone',
  BOTH: 'both'
}
ONE_TIME_CODE_LOGIN_TYPES.ALL = _.values(ONE_TIME_CODE_LOGIN_TYPES)
ONE_TIME_CODE_LOGIN_TYPES.DEFAULT = [ONE_TIME_CODE_LOGIN_TYPES.EMAIL, ONE_TIME_CODE_LOGIN_TYPES.PHONE]

// Sunday = 0, just like javascript date methods
const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function timeSlotKey (dayNumber, timeSlotId) {
  return `${dayNumber},${timeSlotId}`
}

export function parseTimeSlotKey (key) {
  const keys = key.split(',')
  return {
    dayNumber: keys[0],
    timeSlotId: keys[1]
  }
}

export default class Circuit extends Model {
  static name = 'Circuit'

  static isConfiguration = true

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Circuit]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String
      },
      ownerEmail: {
        type: String,
        default: null
      },
      ownerPhone: {
        type: String,
        default: null
      },
      borCcAddresses: {
        type: String,
        default: null
      },
      borBccAddresses: {
        type: String,
        default: null
      },
      dayOfWeekStart: {
        type: Integer,
        options: _.map(daysOfWeek, (day, index) => ({ label: day, value: index })),
        default: 5 // Friday
      },
      boxOfficeWeekDefaultStartDay: {
        type: Integer,
        options: _.map(daysOfWeek, (day, index) => ({ label: day, value: index })),
        default: 5 // Friday
      },
      boxOfficeWeekDefaultExtendPremieres: {
        type: Boolean,
        default: false
      },
      rewardsCurrencyName: {
        type: String,
        default: 'Points',
        translatable: true
      },

      allowGuests: {
        type: Boolean,
        defaut: false
      },
      refundPartialMemberships: {
        type: Boolean,
        defaut: false
      },
      prorateMemberships: {
        type: Boolean,
        defaut: false
      },
      replaceMemberships: {
        type: Boolean,
        defaut: false
      },
      appendMemberships: {
        type: Boolean,
        defaut: false
      },
      allowFriends: {
        type: Boolean,
        defaut: false
      },
      enableMembershipMemberLimit: {
        type: Boolean,
        default: false
      },
      membershipMemberLimit: {
        type: Integer,
        default: 0
      },
      allowMonthlyMembership: {
        type: Boolean,
        default: true
      },
      allowQuarterlyMembership: {
        type: Boolean,
        default: false
      },
      quarterlyDiscountPercentThousandths: {
        type: Integer,
        default: 5 * 1000
      },
      allowBiannualMembership: {
        type: Boolean,
        default: false
      },
      biannualDiscountPercentThousandths: {
        type: Integer,
        default: 10 * 1000
      },
      allowAnnualMembership: {
        type: Boolean,
        default: false
      },
      annualDiscountPercentThousandths: {
        type: Integer,
        default: 20 * 1000
      },
      priceTiersEnabled: {
        type: Boolean,
        default: false
      },
      membershipRateDisplayPeriod: {
        type: Integer,
        default: MEMBERSHIP_RATE_DISPLAY_PERIODS.MONTHLY,
        options: [
          { label: 'Monthly', value: MEMBERSHIP_RATE_DISPLAY_PERIODS.MONTHLY },
          { label: 'Yearly', value: MEMBERSHIP_RATE_DISPLAY_PERIODS.YEARLY }
        ]
      },

      membershipTaxable: {
        type: Boolean,
        default: false
      },
      membershipTaxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      membershipPricesIncludeTax: {
        type: Boolean,
        default: false
      },
      registrationOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          prompt_for_date_of_birth: true,
          require_date_of_birth: false,
          prompt_for_user_class: true,
          prompt_for_phone: true,
          require_user_class: false,
          require_phone: false,
          prompt_for_address: false,
          require_address: false
        })
      },
      adminOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          auto_logout: false,
          auto_logout_minutes: 5
        })
      },
      posOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          show_images_in_cart: true,
          hide_movie_posters_in_ticket_selection: false,
          hide_item_price: false
        })
      },
      kioskOptions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          enable_one_time_code_login: false,
          one_time_code_login_type: ONE_TIME_CODE_LOGIN_TYPES.EMAIL,
          default_one_time_code_login_type: ONE_TIME_CODE_LOGIN_TYPES.EMAIL
        })
      },
      reservedSeatingShowingBadge: {
        type: ShowingBadge,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      currency: {
        type: String,
        default: DEFAULT_CURRENCY,
        options: _.values(CURRENCIES)
      },
      iso31661: {
        type: String,
        default: 'US',
        options: _.values(COUNTRY_CODES)
      },
      additionalLanguages: {
        type: JsonArray,
        default: () => ([]),
        required: false
      },
      isTemplate: {
        type: Boolean,
        default: false
      },
      action: {
        type: String,
        default: null,
        input: true
      },
      defaultDashboard: {
        type: Report,
        reference: true,
        encode: encoders.hasOne
      },
      systemReportIdMap: {
        type: JsonObject,
        output: true
      },
      accountManagers: {
        type: [AccountManager],
        allowDestroy: true,
        excludeFromDefault: true
      },
      stripeCancellationPolicyFileId: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      stripeRefundPolicyFileId: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      enableTransfers: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      enableRenewals: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      giftCardExpiryOptions: {
        type: JsonObject,
        default: { has_expiry: false, expiry_value: 3, expiry_term: 'years' }
      },

      defaultItemCostPercent: {
        type: Integer,
        default: null
      },
      defaultTicketCostPercent: {
        type: Integer,
        default: null
      },
      sites: {
        type: [Site],
        reference: true,
        default () {
          return []
        }
      },
      locations: {
        type: [Location],
        output: true
      },
      customerioWorkspaceId: {
        type: Integer,
        excludeFromDefault: true,
        default: null
      },
      customerioSiteId: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      customerioTrackingApiKey: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      customerioAppApiKey: {
        type: String,
        excludeFromDefault: true,
        default: null
      },
      enableSms: {
        type: Boolean,
        default: false
      },

      customerioWorkspaceIsConfigured: {
        type: Boolean,
        excludeFromDefault: true,
        output: true
      },
      cardPrefixesToStrip: {
        type: JsonArray,
        default: () => ([]),
        required: false
      },
      cardSuffixesToStrip: {
        type: JsonArray,
        default: () => ([]),
        required: false
      },
      isTravellingCinema: {
        type: Boolean,
        excludeFromDefault: true,
        default: false
      },
      hasMembershipVouchers: {
        type: Boolean,
        excludeFromDefault: true,
        output: true
      },
      isMultisite: {
        type: Boolean,
        required: true,
        output: true
      },
      migratedTo: {
        type: String,
        output: true
      },
      allowFilmChiefIntegration: {
        type: Boolean,
        default: false
      },
      userPinLength: {
        type: Integer,
        excludeFromDefault: true,
        default: 4
      },
      cardPrefixesRequiringPin: {
        type: JsonObject,
        default: () => ({}),
        output: true
      }
    }

    this.queries = {
      currentCircuit: {
        results: {
          circuit: Circuit
        }
      }
    }
  }

  get supportsTranslations () {
    return !_.isEmpty(this.additionalLanguages)
  }

  get availableLanguages () {
    return [EN, ...this.additionalLanguages]
  }

  /**
   * Converts the base language locale codes to language-region codes. For each language, prefers
   * the circuit's country locale instead of the system default, if it's specifically supported.
   */
  get availableLanguageRegions () {
    return _.map(this.availableLanguages, (lang) => {
      const maybeAvailableLanguageRegion = `${lang}-${_.toUpper(this.iso31661)}`
      if (_.includes(REGION_AVAILABLE_LOCALES, maybeAvailableLanguageRegion)) {
        return maybeAvailableLanguageRegion
      } else {
        return findCompatibleLanguageRegion(lang)
      }
    })
  }

  get availableLocales () {
    return _.uniq([...this.availableLanguages, ...this.availableLanguageRegions])
  }

  get availableLanguagesOptions () {
    return _.map(this.availableLanguages, lang => ({ label: this.i18n.t(`languages.${lang}`), value: lang }))
  }

  get additionalLanguagesOptions () {
    const nonEnglishLanguages = _(REGION_AVAILABLE_LANGUAGES).values().without(EN).value()
    return _.map(nonEnglishLanguages, lang => ({ label: this.i18n.t(`languages.${lang}`), value: lang }))
  }

  // Comparing the following translation outputs against the keys is a hack to
  // determine if the translation is missing. There is a bug with the i18n.te()
  // function that is supposed to help with checking if key exists, but doesn't
  // work for nested keys. When a translation is missing, the key string is returned.
  get localeCurrencySymbol () {
    let key = `currency.${this.currency}.symbol`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    key = `currency.${DEFAULT_CURRENCY}.symbol`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    return '$'
  }

  get localeCurrencyHundredthsSymbol () {
    let key = `currency.${this.currency}.hundredthsSymbol`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    key = `currency.${DEFAULT_CURRENCY}.hundredthsSymbol`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    return '¢'
  }

  get localeCurrencyName () {
    let key = `currency.${this.currency}.name`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    key = `currency.${DEFAULT_CURRENCY}.name`
    if (this.i18n.t(key) !== key) {
      return this.i18n.t(key)
    }
    return 'Dollar'
  }

  get daysOfWeek () {
    return _.map(this.daysOfWeekNumbers, day => daysOfWeek[day].substring(0, 3))
  }

  get daysOfWeekFull () {
    return _.map(this.daysOfWeekNumbers, day => daysOfWeek[day])
  }

  get daysOfWeekNumbers () {
    const result = []

    for (let day = this.dayOfWeekStart; day < this.dayOfWeekStart + 7; day += 1) {
      result.push(day % 7)
    }

    return result
  }

  get hasMultipleMembershipTerms () {
    return _.compact([
      this.allowMonthlyMembership ? true : null,
      this.allowQuarterlyMembership ? true : null,
      this.allowBiannualMembership ? true : null,
      this.allowAnnualMembership ? true : null
    ]).length > 1
  }

  get defaultMembershipTerm () {
    const terms = _.compact([
      this.allowMonthlyMembership ? 1 : null,
      this.allowQuarterlyMembership ? 3 : null,
      this.allowBiannualMembership ? 6 : null,
      this.allowAnnualMembership ? 12 : null
    ])
    return terms.length ? terms[0] : 1
  }

  get rewards () {
    return _.filter(this.configuration.voucherTypes.data, voucherType => voucherType.isReward)
  }

  get isLibrary () {
    return this.id === 0
  }

  get membershipRateTypeDisplay () {
    switch (this.membershipRateTypeDisplay) {
      case MEMBERSHIP_RATE_DISPLAY_PERIODS.MONTHLY:
        return 'month'
      case MEMBERSHIP_RATE_DISPLAY_PERIODS.YEARLY:
        return 'year'
      default:
        return 'month'
    }
  }

  get allowTaxDeductibleDonations () {
    return isUSBasedRegion
  }

  reportIdForName (systemName) {
    return this.systemReportIdMap[systemName]
  }
}
